export type StandaloneGameType = 'flipCardGame' | 'quizMarathon' | 'doubleDown';
export type ActiveGameMode = 'standalone' | 'hop';

export interface ActiveGameContextV1 {
  version: 1;
  userId: number | null;
  mode: ActiveGameMode;
  gameType: StandaloneGameType;
  sessionId: number;
  gameRoomId: number | null;
  issuedAt: number;
  updatedAt: number;
}

export type ActiveGameSessionResponse =
  | { state: 'none' }
  | {
      state: 'lobby' | 'starting' | 'playing';
      sessionId: number;
      gameType: StandaloneGameType;
      gameRoomId: number | null;
      playerId: number;
      statusUpdatedAt: string;
    }
  | {
      state: 'terminal';
      sessionId: number;
      gameType: string;
      outcome: 'completed' | 'cancelled' | 'abandoned';
      endReason: string;
      resultsAvailable: boolean;
    };

export class ActiveGameLookupError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'ActiveGameLookupError';
  }
}

const ACTIVE_GAME_CONTEXT_KEY = 'activeGameContext:v1';
export const ACTIVE_GAME_RESOLUTION_EVENT = 'kokki:active-game-resolution';
export const ACTIVE_GAME_RESOLUTION_ERROR_KEY = 'activeGameResolutionError';
const MAX_CONTEXT_AGE_MS = 24 * 60 * 60 * 1000;
const GAME_TYPES = new Set<StandaloneGameType>([
  'flipCardGame',
  'quizMarathon',
  'doubleDown',
]);

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const isGameType = (value: unknown): value is StandaloneGameType => (
  typeof value === 'string' && GAME_TYPES.has(value as StandaloneGameType)
);

export const readActiveGameContext = (): ActiveGameContextV1 | null => {
  const raw = localStorage.getItem(ACTIVE_GAME_CONTEXT_KEY);
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<ActiveGameContextV1>;
    const valid = value.version === 1
      && (value.userId === null || isFiniteNumber(value.userId))
      && (value.mode === 'standalone' || value.mode === 'hop')
      && isGameType(value.gameType)
      && isFiniteNumber(value.sessionId)
      && (value.gameRoomId === null || isFiniteNumber(value.gameRoomId))
      && isFiniteNumber(value.issuedAt)
      && isFiniteNumber(value.updatedAt);

    if (!valid || Date.now() - value.updatedAt! > MAX_CONTEXT_AGE_MS) {
      localStorage.removeItem(ACTIVE_GAME_CONTEXT_KEY);
      return null;
    }

    return value as ActiveGameContextV1;
  } catch {
    localStorage.removeItem(ACTIVE_GAME_CONTEXT_KEY);
    return null;
  }
};

export const writeActiveGameContext = (
  input: Omit<ActiveGameContextV1, 'version' | 'issuedAt' | 'updatedAt'>
    & Partial<Pick<ActiveGameContextV1, 'issuedAt' | 'updatedAt'>>,
): ActiveGameContextV1 => {
  const previous = readActiveGameContext();
  const now = Date.now();
  const context: ActiveGameContextV1 = {
    version: 1,
    ...input,
    issuedAt: input.issuedAt
      ?? (previous?.sessionId === input.sessionId && previous.mode === input.mode
        ? previous.issuedAt
        : now),
    updatedAt: input.updatedAt ?? now,
  };
  localStorage.setItem(ACTIVE_GAME_CONTEXT_KEY, JSON.stringify(context));
  return context;
};

export const clearStandaloneGameContext = () => {
  const context = readActiveGameContext();
  if (context?.mode === 'hop') return;
  localStorage.removeItem(ACTIVE_GAME_CONTEXT_KEY);
  localStorage.removeItem('currentGameId');
  localStorage.removeItem('currentGameType');
  localStorage.removeItem('standaloneGameData');
};

export const clearAllGameContexts = () => {
  localStorage.removeItem(ACTIVE_GAME_CONTEXT_KEY);
  localStorage.removeItem('currentGameId');
  localStorage.removeItem('currentGameType');
  localStorage.removeItem('standaloneGameData');
  sessionStorage.removeItem('hopGameData');
  sessionStorage.removeItem(ACTIVE_GAME_RESOLUTION_ERROR_KEY);
  sessionStorage.removeItem('dismissedGameSessionTerminalId');
};

export const clearHopGameContext = () => {
  const context = readActiveGameContext();
  if (!context || context.mode === 'hop') {
    localStorage.removeItem(ACTIVE_GAME_CONTEXT_KEY);
  }
  localStorage.removeItem('currentGameId');
  localStorage.removeItem('currentGameType');
  sessionStorage.removeItem('hopGameData');
};

export const readHopGameData = (expectedUserId?: number | null): any | null => {
  const raw = sessionStorage.getItem('hopGameData');
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    const updatedAt = Number(value?.updatedAt);
    if (
      !value
      || !isFiniteNumber(Number(value.gameRoomId))
      || !isGameType(value.gameType)
      || !Number.isFinite(updatedAt)
      || Date.now() - updatedAt > MAX_CONTEXT_AGE_MS
      || (expectedUserId != null && Number(value.userId) !== expectedUserId)
    ) {
      sessionStorage.removeItem('hopGameData');
      return null;
    }
    return value;
  } catch {
    sessionStorage.removeItem('hopGameData');
    return null;
  }
};

export const fetchActiveGameSession = async (
  backendUrl: string,
  token: string,
): Promise<ActiveGameSessionResponse> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`${backendUrl}game-session/active`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new ActiveGameLookupError(
        `Active game lookup failed with ${response.status}`,
        response.status,
      );
    }
    const payload = await response.json() as ActiveGameSessionResponse;
    if (!payload || typeof payload !== 'object' || !('state' in payload)) {
      throw new Error('Active game lookup returned an invalid payload');
    }
    sessionStorage.removeItem(ACTIVE_GAME_RESOLUTION_ERROR_KEY);
    window.dispatchEvent(new CustomEvent(ACTIVE_GAME_RESOLUTION_EVENT, {
      detail: { unavailable: false },
    }));
    return payload;
  } catch (error) {
    if (
      error instanceof ActiveGameLookupError
      && (error.status === 401 || error.status === 403)
    ) {
      localStorage.removeItem('token');
      clearAllGameContexts();
      window.dispatchEvent(new CustomEvent(ACTIVE_GAME_RESOLUTION_EVENT, {
        detail: { unavailable: false },
      }));
      throw error;
    }
    sessionStorage.setItem(ACTIVE_GAME_RESOLUTION_ERROR_KEY, '1');
    window.dispatchEvent(new CustomEvent(ACTIVE_GAME_RESOLUTION_EVENT, {
      detail: { unavailable: true },
    }));
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

export const acknowledgeTerminalGameSession = async (
  backendUrl: string,
  token: string,
  sessionId: number,
): Promise<boolean> => {
  const response = await fetch(`${backendUrl}game-session/active/ack`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sessionId }),
  });
  if (!response.ok) {
    throw new ActiveGameLookupError(
      `Terminal acknowledgement failed with ${response.status}`,
      response.status,
    );
  }
  const payload = await response.json() as { acknowledged?: boolean };
  return payload.acknowledged === true;
};

export const isUnrecoverableGameJoinError = (message?: string | null) => {
  const normalized = String(message || '').trim().toLowerCase();
  return normalized.includes('game not found')
    || normalized.includes('player not found')
    || normalized.includes('not an active player')
    || normalized.includes('identity does not match')
    || normalized.includes('session has already completed')
    || normalized.includes('game has already completed')
    || normalized.includes('game completed');
};

export const isSocketAuthenticationError = (message?: string | null) => {
  const normalized = String(message || '').trim().toLowerCase();
  return normalized.includes('jwt expired')
    || normalized.includes('invalid token')
    || normalized.includes('invalid signature')
    || normalized.includes('authentication required')
    || normalized.includes('no authentication provided')
    || normalized === 'unauthorized';
};

export const syncContextFromActiveSession = (
  active: Extract<ActiveGameSessionResponse, { state: 'lobby' | 'starting' | 'playing' }>,
  userId: number | null,
) => {
  sessionStorage.removeItem('dismissedGameSessionTerminalId');
  const context = writeActiveGameContext({
    userId,
    mode: 'standalone',
    gameType: active.gameType,
    sessionId: active.sessionId,
    gameRoomId: active.gameRoomId,
  });

  // Transitional compatibility cache for game components that still consume
  // standaloneGameData. It is reconstructed solely from the authenticated
  // resolver and is never used by route guards as an authority.
  localStorage.setItem('standaloneGameData', JSON.stringify({
    sessionId: active.sessionId,
    gameRoomId: active.gameRoomId,
    gameType: active.gameType,
    myPlayerId: userId !== null ? String(userId) : String(active.playerId),
  }));
  if (active.gameRoomId !== null) {
    localStorage.setItem('currentGameId', String(active.gameRoomId));
    localStorage.setItem('currentGameType', active.gameType);
  }
  return context;
};
